import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';


import { endConference, updateConferenceCallEnded } from '../../../base/conference';
import { disconnect } from '../../../base/connection';
import { openDialog } from '../../../base/dialog';
import { Icon, IconHangup } from '../../../base/icons';
import { getYoutubeParticipant, kickParticipant } from '../../../base/participants';
import { getBrandingColorPalette } from '../../../base/styles';

import { AlertBox } from './AlertBox';
import { colorsHangup } from './Constants';

const styles = (interfaceConfig = {}) => {
    const branding = getBrandingColorPalette(interfaceConfig);
    const hangupColors = colorsHangup(interfaceConfig);

    return {
        hangUpEveryOne: {
            backgroundColor: interfaceConfig?.applyMeetingSettings ? branding.accent2 : hangupColors.hangUpRed
        },
        hangUpAlone: {
            backgroundColor: interfaceConfig?.applyMeetingSettings ? branding.secondary : branding.primary
        },
        whiteColor: {
            color: interfaceConfig?.applyMeetingSettings ? branding.accent1 : hangupColors.white
        }
    };
};

type Props = {
    onLeaveEveryOne: ?Function,
    onClose?: ?Function
}


export const HangupContent = (props: Props) => {
    const { onLeaveEveryOne, onClose } = props;
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const participants = useSelector(state => state['features/base/participants'] || []);
    const interfaceConfig = useSelector(state => state['features/base/config']?.interfaceConfig);
    const rooms = useSelector(state => state['features/breakout-rooms']?.rooms || {});
    const isRemoteParticipants = participants.filter(it => it.local !== true && !it?.isFakeParticipant);
    const howManyModerators = participants.filter(it => it.role === 'moderator' && !it?.isFakeParticipant);

    const onJustHangUp = React.useCallback((isTrue = false) => {
        if (howManyModerators.length <= 1 && !isTrue && isRemoteParticipants.length > 0) {
            dispatch(openDialog(AlertBox, {
                alertText: 'dialog.HangupSingleLeaveReason',
                disableOverlay: false,
                submitDisabled: false,
                cancelDisabled: false,
                onSubmit: () => onJustHangUp(true),
                okKey: 'dialog.endAnyway'
            }));

            return;
        }

        if (onClose) {
            onClose();
        }

        dispatch(disconnect(false));
        dispatch(updateConferenceCallEnded(true));
    }, [ dispatch, howManyModerators.length, isRemoteParticipants.length, onClose ]);


    const youtubeParticipant = useSelector(state => getYoutubeParticipant(state));
    const conference = useSelector(state => state['features/base/conference']?.conference);

    const hitFunc = React.useCallback(index => {
        setTimeout(() => {
            const element = isRemoteParticipants[index];
            const translated = t('dialog.hangUpLeaveReason');

            if (element?.id) {
                dispatch(kickParticipant(element.id, translated));
            }
        }, index * 300);
    }, [ isRemoteParticipants, dispatch, t ]);

    const onRemoveEveryOneAndLeave = React.useCallback(() => {
        if (onLeaveEveryOne) {
            onLeaveEveryOne(true);
        }

        // Ending the meeting is the server's job: prosody destroys the room, which
        // removes everyone in one step and reaches the people this component
        // cannot see -- visitors on their own nodes, occupants of the backstage
        // room, and anyone in a breakout room. The local participant is destroyed
        // with the room too, so there is no hangup to schedule afterwards.
        if (dispatch(endConference())) {
            return;
        }

        // No end_conference component in this deployment. Fall back to kicking
        // each participant from here, which is the best a client can do: it only
        // reaches the main room, and it takes a round trip per person.
        for (let index = 0; index < isRemoteParticipants.length; index++) {
            hitFunc(index);
        }
        if (youtubeParticipant) {
            dispatch(kickParticipant(youtubeParticipant?.id));
        }

        const breakoutRooms = Object.values(rooms).filter(room => !room.isMainRoom);
        const breakoutRoomsManager = conference?.getBreakoutRooms();

        if (breakoutRoomsManager) {
            breakoutRooms.forEach(room => {
                if (room.jid) {
                    breakoutRoomsManager.removeBreakoutRoom(room.jid);
                }
            });
        }

        const hangupDelay = (isRemoteParticipants.length * 300) + 500;

        setTimeout(() => {
            onJustHangUp(true);
        }, hangupDelay);
    }, [ dispatch, onLeaveEveryOne, isRemoteParticipants, hitFunc, youtubeParticipant, conference, onJustHangUp, rooms ]);

    const computedStyles = styles(interfaceConfig);

    return (
        <div className = 'hangup-popup-container'>
            <div className = 'hangup-main-view'>
                <div
                    className = 'hangup-item-btn hangup-alone-btn'
                    onClick = { () => onJustHangUp(false) }
                    style = { computedStyles.hangUpAlone }>
                    <Icon
                        className = 'hangup-btn-icon'
                        size = { 18 }
                        src = { IconHangup } />
                    <span
                        className = 'hangup-btn-label'
                        style = { computedStyles.whiteColor }>
                        {t('toolbar.hangUpforMe')}
                    </span>
                </div>
                {
                    Array.isArray(isRemoteParticipants) && isRemoteParticipants.length > 0 && (
                        <div
                            className = 'hangup-item-btn hangup-everyone-btn'
                            onClick = { onRemoveEveryOneAndLeave }
                            style = { computedStyles.hangUpEveryOne }>
                            <Icon
                                className = 'hangup-btn-icon'
                                size = { 18 }
                                src = { IconHangup } />
                            <span
                                className = 'hangup-btn-label'
                                style = { computedStyles.whiteColor }>
                                {t('toolbar.hangUpforEveryOne')}
                            </span>
                        </div>
                    )
                }
            </div>
        </div>
    );
};

